import { useState, useRef, useEffect } from "react";
import CreateTab from "./CreateTab";
import LibraryTab from "./LibraryTab";
import { useNavigate, useLocation } from "react-router-dom";

export default function CharacterLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("library");
  const [selectedCustomCharacter, setSelectedCustomCharacter] = useState(null);
  const createTabRef = useRef(null);
  const [selectedTemplateCharacter, setSelectedTemplateCharacter] =
    useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  const hasSelectedCharacter =
    selectedCustomCharacter !== null || selectedTemplateCharacter !== null;

  const handleClearSelection = () => {
    setSelectedCustomCharacter(null);
    setSelectedTemplateCharacter(null);
  };

  return (
    <div className="responsive-page responsive-page--characters min-h-screen bg-gray-50 dark:bg-[#0a0a0a]">
      {/* Main Content */}
      <main className="flex-1 bg-gray-50 dark:bg-[#0a0a0a]">
        <div className="max-w-[1800px] mx-auto p-6">
          <div className="character-library-header flex items-center justify-between mb-5">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {activeTab === "create"
                ? "Create Custom Character"
                : "Character Library"}
            </h1>

            <div className="character-library-actions flex gap-2">
              {activeTab === "library" && (
                <div className="character-library-search relative">
                  <i
                    data-lucide="search"
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400"
                  ></i>
                  <input
                    type="text"
                    placeholder="Search characters..."
                    aria-label="Search characters"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 w-64 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-orange-500/50"
                  />
                </div>
              )}
              <button
                onClick={() => {
                  setActiveTab("create");
                  handleClearSelection();
                }}
                className={`character-library-tab px-5 py-2 rounded-lg font-medium transition-all ${activeTab === "create"
                  ? "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
                  : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
                  }`}
              >
                <span className="character-library-tab-long">Create Custom Character</span>
                <span className="character-library-tab-short">Create character</span>
              </button>
              <button
                onClick={() => {
                  // If we're on the create tab, let CreateTab guard the switch
                  if (activeTab === "create" && createTabRef.current) {
                    createTabRef.current.requestTabChange("library");
                  } else {
                    setActiveTab("library");
                  }
                  handleClearSelection();
                }}
                className={`character-library-tab px-5 py-2 rounded-lg font-medium transition-all ${activeTab === "library"
                  ? "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
                  : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
                  }`}
              >
                <span className="character-library-tab-long">Character Library</span>
                <span className="character-library-tab-short">Library</span>
              </button>
            </div>
          </div>

          {activeTab === "create" && <CreateTab ref={createTabRef} setActiveTab={setActiveTab} />}
          {activeTab === "library" && (
            <LibraryTab
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedCustomCharacter={selectedCustomCharacter}
              selectedTemplateCharacter={selectedTemplateCharacter}
              onSelectedCustomCharacterChange={setSelectedCustomCharacter}
              onSelectedTemplateCharacterChange={setSelectedTemplateCharacter}
            />
          )}
        </div>
      </main>

      {hasSelectedCharacter && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 dark:border-white/10 bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-end">
            <button
              onClick={() => {
                const selectedId = selectedCustomCharacter || selectedTemplateCharacter;
                const returnTo = location.state?.returnTo;
                if (returnTo) {
                  navigate(returnTo, {
                    state: {
                      ...location.state,
                      selectedCharacterId: selectedId,
                      // A selection made in the full library is promoted to
                      // the first character card in the destination studio.
                      characterSelectionSource: "all-characters",
                    },
                  });
                } else {
                  navigate("/app/speaking-character", {
                    state: {
                      selectedCharacterId: selectedId,
                      characterSelectionSource: "all-characters",
                    },
                  });
                }
              }}
              className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold shadow-lg shadow-orange-500/30 rounded-xl flex items-center gap-2"
            >
              <i data-lucide="sparkles" className="h-5 w-5"></i>
              Use Selected Character
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
